import { useState } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { IconButton, Tooltip, Button } from '@material-ui/core';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useDispatch } from 'react-redux';
import { setOpenSnackbar } from '../../store/slices/snackbarSlice';
import ConfirmDialog from '../ConfirmDialog';
import { getIDToken } from '../authentication/login/amplify';

RemoveRoleButton.propTypes = {
  variant: PropTypes.string,
  context: PropTypes.oneOf(['team', 'user']).isRequired,
  target: PropTypes.string.isRequired, // team name or username
  role: PropTypes.string.isRequired,
  updateTable: PropTypes.func.isRequired,
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object])),
    PropTypes.func,
    PropTypes.object
  ]),
  disabled: PropTypes.bool
};

export default function RemoveRoleButton({
  variant,
  context,
  target,
  role,
  updateTable,
  sx = [],
  disabled
}) {
  const dispatch = useDispatch();
  const [confirmDialogOpen, setDialogOpen] = useState(false);
  const openSnackbar = (payload) => dispatch(setOpenSnackbar(payload));

  const getConfig = () => {
    if (context === 'team') {
      return {
        endpoint: `${process.env.REACT_APP_API}/admin/teams/roles`,
        requestBody: { team: target, roles: [role] },
        successMessage: 'Role removed from team successfully!',
        errorMessage: 'Failed to remove role from team',
        logMessage: `Deleting role: ${role} from team: ${target}`,
        confirmContent: 'Are you sure you wish to remove the specified role from the team?'
      };
    }
    return {
      endpoint: `${process.env.REACT_APP_API}/admin/users/roles`,
      requestBody: { username: target, roles: [role] },
      successMessage: 'Role removed from user successfully!',
      errorMessage: 'Failed to remove role from user',
      logMessage: `Deleting role: ${role} from user: ${target}`,
      confirmContent: 'Are you sure you wish to remove the specified role from the user?'
    };
  };

  const deleteRole = async () => {
    const config = getConfig();
    console.debug(config.logMessage);

    const jwt = await getIDToken();
    if (jwt) {
      axios({
        method: 'delete',
        url: config.endpoint,
        data: config.requestBody,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` }
      })
        .then((response) => {
          console.debug(
            `Successfully removed role from ${context}: ${JSON.stringify(response.data)}`
          );
          openSnackbar({ message: config.successMessage });
          updateTable();
          return response.data;
        })
        .catch((error) => {
          console.error(error.message);
          openSnackbar({ message: config.errorMessage, severity: 'error' });
        });
    } else {
      console.error(`JWT has expired, cannot remove role from ${context}`);
    }
  };

  const handleDialogCancel = () => {
    setDialogOpen(false);
  };

  const handleDialogConfirm = () => {
    deleteRole();
    setDialogOpen(false);
  };

  const handleDialogOpen = (event) => {
    event.stopPropagation();
    setDialogOpen(true);
  };

  const config = getConfig();

  return (
    <>
      <ConfirmDialog
        title="Delete Role?"
        content={config.confirmContent}
        onCancelHandler={handleDialogCancel}
        onConfirmHandler={handleDialogConfirm}
        isOpen={confirmDialogOpen}
      />
      {variant === 'icononly' && (
        <Tooltip title="Delete Role">
          <IconButton onClick={handleDialogOpen} sx={{ ...sx }} disabled={disabled} color="primary">
            <RemoveCircleOutlineIcon data-testid="delete-icon" />
          </IconButton>
        </Tooltip>
      )}
      {variant === 'text' && (
        <Tooltip title="Delete Role">
          <Button
            onClick={handleDialogOpen}
            variant="contained"
            sx={{ ...sx }}
            disabled={disabled}
            startIcon={<RemoveCircleOutlineIcon />}
          >
            Delete Role
          </Button>
        </Tooltip>
      )}
    </>
  );
}
